import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const MUTED = "#94a3b8";

const trustNavLinks = [
  { href: "/methodology", label: "Methodology" },
  { href: "/process-controls", label: "Process Controls" },
  { href: "/deliverables", label: "Deliverables" },
  { href: "/compliance-data-handling", label: "Compliance" },
  { href: "/document-library", label: "Documents" },
  { href: "/change-log", label: "Change Log" },
];

interface TrustNavigationProps {
  currentPath?: string;
}

export function TrustNavigation({ currentPath }: TrustNavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}>
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link href="/" className="flex items-center gap-3">
          <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" data-testid="logo-nav" />
          <span className="text-white font-bold tracking-widest text-sm">VEXIS</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {trustNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm font-medium transition-colors"
              style={{ color: currentPath === link.href ? "#ffffff" : MUTED }}
              onMouseEnter={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseLeave={e => (e.currentTarget.style.color = currentPath === link.href ? "#ffffff" : MUTED)}
              data-testid={`link-nav-${link.href.replace("/", "")}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex gap-3 items-center">
          <Link href="/contact">
            <Button
              className="font-semibold text-white"
              style={{ backgroundColor: "#6366f1" }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#6366f1")}
              data-testid="button-nav-contact"
            >
              Submit Enquiry
            </Button>
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          style={{ color: "#ffffff" }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          data-testid="button-mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }} data-testid="mobile-menu">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {trustNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-medium py-2 transition-colors"
                style={{ color: currentPath === link.href ? "#ffffff" : MUTED }}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`link-mobile-${link.href.replace("/", "")}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-4" style={{ borderTop: "1px solid rgba(99,102,241,0.15)" }}>
              <Link href="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full font-medium text-slate-300 hover:text-white hover:bg-white/10">
                  Home
                </Button>
              </Link>
              <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full font-semibold text-white" style={{ backgroundColor: "#6366f1" }} data-testid="button-mobile-contact">
                  Submit Enquiry
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
